import { useApiData } from 'lib/api';
import { useReduxState } from 'hooks';
import { LoadingIndicator, User } from 'components/common';
import { Box, BoxBackLink } from 'components/common/box';
import {
  ProfileCourseChapters,
  ProfileCoursePreviousCompletions,
  ProfileCourseProgress,
  ProfileCoursesCourseTasks
} from 'components/profile';

import type { GetTaskUsersRes } from 'shared-types/responses';

const FollowUser = () => {
  const {
    messages,
    pageState: { course, userId },
    user: loggedUser
  } = useReduxState();

  const { data, loading, updateData } = useApiData<GetTaskUsersRes>({
    url: `/admin/courses/${course.id}/task-users?id=${userId}`,
    onlyIf: Boolean(userId)
  });

  const {
    courseVersionTagsByVersionNumber,
    chapters,
    tasks,
    tasksOrder,
    users
  } = data || {};

  const user = users?.[0];

  return loading ? (
    <LoadingIndicator />
  ) : !user ? null : (
    <div>
      <div className="user">
        <Box
          padding={20}
          title={
            <BoxBackLink
              as={`/admin/courses/${course.slug}/follow/users`}
              href="/admin/courses/[courseSlug]/follow/users"
              text={messages.usersAllUsers}
            />
          }
        >
          {course ? (
            <div className="summary">
              <div className="header">
                <User enableProfileLink={true} user={user} />
              </div>

              <div className="progress">
                <ProfileCourseProgress
                  allowDownloadingCertificate={true}
                  course={course}
                  deadline={user.deadline}
                  lastOpenedDate={user.lastOpenedDate}
                  progressData={user.progressData}
                  relatedPathDeadlines={user.relatedPathDeadlines}
                  userId={user.id}
                />
              </div>
            </div>
          ) : null}
        </Box>
      </div>

      {user?.previousCourseCompletions?.length ? (
        <div className="previous-completions">
          <ProfileCoursePreviousCompletions
            certificateAllowDownloading={course?.certificateAllowDownloading}
            completionsAllowViewing={true}
            courseLanguageVersions={course?.languageVersions}
            courseSlug={course?.slug}
            courseVersionTagsByVersionNumber={courseVersionTagsByVersionNumber}
            disableActions={false}
            isUserPage={true}
            previousCourseCompletions={user?.previousCourseCompletions}
            tasksAllowDownloading={course?.tasksAllowDownloading}
            tasksIncludeReviews={course?.tasksIncludeReviews}
            updateUser={updateData}
            user={user}
          />
        </div>
      ) : null}

      <div className="chapters">
        <ProfileCourseChapters
          chapters={chapters}
          languageVersion={course.language}
          progressData={user.progressData}
        />
      </div>

      <div className="tasks">
        <ProfileCoursesCourseTasks
          chapters={chapters}
          course={course}
          isUserPage={true}
          loggedUserHasEmail={Boolean(loggedUser?.email)}
          onDataChange={updateData}
          tasks={tasks}
          tasksAllowDownloading={true}
          tasksOrder={tasksOrder}
          user={user}
          userTasksData={user?.tasksData}
        />
      </div>

      <style jsx>{`
        .progress {
          margin-top: 30px;
        }
        .previous-completions {
          margin-top: 30px;
        }
        .chapters {
          margin-top: 30px;
        }
        .tasks {
          margin-top: 30px;
        }
      `}</style>
    </div>
  );
};

export default FollowUser;
